import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import useUnits from '../hooks/useUnits';
import useDebouncedValue from '../hooks/useDebouncedValue';

const MenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    categoria: 'prato principal',
    unidade: '',
    dataPreparacao: '',
    imagem: '',
    ativo: true,
    ordem: 0
  });

  const categories = [
    { value: 'prato principal', label: 'Prato Principal' },
    { value: 'arroz', label: 'Arroz' },
    { value: 'feijao', label: 'Feijão' },
    { value: 'massa', label: 'Massa' },
    { value: 'salada', label: 'Salada' },
    { value: 'guarnição', label: 'Guarnição' },
    { value: 'sobremesa', label: 'Sobremesa' },
    { value: 'suco', label: 'Suco' }
  ];

  const { units, loading: unitsLoading } = useUnits();
  // Filtros e ordenação
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterLoading, setFilterLoading] = useState(false);
  const [multiDateMode, setMultiDateMode] = useState(false);
  const [multiDates, setMultiDates] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('');
  // const [useDishMode, setUseDishMode] = useState(true); // Removed manual mode
  const [dishOptions, setDishOptions] = useState([]);
  const [dishLoading, setDishLoading] = useState(false);
  const [dishSearch, setDishSearch] = useState('');
  const [dishCategory, setDishCategory] = useState('');
  const [selectedDishId, setSelectedDishId] = useState('');
  const debouncedDishSearch = useDebouncedValue(dishSearch, 500);
  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState([]);

  const loadMenuItems = useCallback(async () => {
    setLoading(true);
    setFilterLoading(true);
    try {
      const items = await adminService.getMenuItems({
        search: appliedSearch || undefined,
        category: selectedCategory || undefined,
        sortBy,
        sortOrder,
        limit: 1000,
        page: 1,
        unit: selectedUnit || undefined,
      });
      setMenuItems(items);
    } catch (error) {
      console.error('Error loading menu items:', error);
      showToast('Erro ao carregar itens do menu', 'error');
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  }, [appliedSearch, selectedCategory, sortBy, sortOrder, selectedUnit]);

  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  const loadDishOptions = useCallback(async () => {
    if (!showForm) return; // Always load dish options when form is shown
    setDishLoading(true);
    try {
      const items = await adminService.getDishes({
        search: debouncedDishSearch || undefined,
        category: dishCategory || undefined,
        sortBy: 'nome',
        sortOrder: 'asc',
        limit: 1000,
        page: 1
      });
      setDishOptions(items);
    } catch (error) {
      console.error('Error loading dishes for menu form:', error);
      showToast('Erro ao carregar pratos', 'error');
    } finally {
      setDishLoading(false);
    }
  }, [showForm, debouncedDishSearch, dishCategory]);

  useEffect(() => {
    loadDishOptions();
  }, [loadDishOptions]);

  const handleDishSelect = (e) => {
    const id = e.target.value;
    setSelectedDishId(id);
    const dish = dishOptions.find(d => d._id === id);
    if (dish) {
      setFormData(prev => ({
        ...prev,
        nome: dish.nome || '',
        descricao: dish.descricao || '',
        categoria: dish.categoria || 'prato principal',
        imagem: dish.imagem || ''
      }));
      setFormErrors(prev => {
        const next = { ...prev };
        delete next.nome;
        delete next.descricao;
        delete next.categoria;
        delete next.imagem;
        delete next.dish;
        return next;
      });
      setHasUnsavedChanges(true);
    }
  };



  // Form validation functions
  const validateField = (name, value) => {
    const errors = {};
    
    switch (name) {
      case 'nome':
        if (!value.trim()) {
          errors.nome = 'Nome é obrigatório';
        } else if (value.trim().length < 3) {
          errors.nome = 'Nome deve ter pelo menos 3 caracteres';
        } else if (value.trim().length > 100) {
          errors.nome = 'Nome deve ter no máximo 100 caracteres';
        }
        break;
      // case 'descricao': validation removed as description is optional
      case 'unidade':
        if (!value) {
          errors.unidade = 'Unidade é obrigatória';
        }
        break;
      case 'dataPreparacao':
        if (!value) {
          errors.dataPreparacao = 'Data de preparação é obrigatória';
        } else {
          const selectedDate = toLocalDate(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
            errors.dataPreparacao = 'Data não pode ser anterior a hoje';
          }
        }
        break;
      case 'imagem':
        if (value && !isValidUrl(value)) {
          errors.imagem = 'URL da imagem inválida';
        }
        break;
      // case 'ordem': validation removed
      default:
        // No validation needed for other fields
        break;
    }
    
    return errors;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate unit and date (common for all modes)
    if (!formData.unidade) errors.unidade = 'Unidade é obrigatória';
    
    if (multiDateMode) {
      if (multiDates.length === 0) {
        errors.dataPreparacao = 'Selecione pelo menos uma data';
      }
    } else {
      if (!formData.dataPreparacao) {
        errors.dataPreparacao = 'Data de preparação é obrigatória';
      } else {
        const selectedDate = toLocalDate(formData.dataPreparacao);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          errors.dataPreparacao = 'Data não pode ser anterior a hoje';
        }
      }
    }

    // Require dish selection for new items
    if (!editingItem && !selectedDishId) {
      errors.dish = 'Selecione um prato cadastrado';
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    setHasUnsavedChanges(true);
    
    // Only run validation after user has interacted with the form
    const fieldErrors = validateField(name, newValue);
    setFormErrors(prev => {
      const newErrors = { ...prev, ...fieldErrors };
      
      // Remove the field from errors if there's no error
      if (!fieldErrors[name]) {
        delete newErrors[name];
      }
      
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showToast('Por favor, corrija os erros no formulário', 'error');
      return;
    }
    
    setFormLoading(true);
    
    try {
      if (editingItem) {
        // Update existing item
        await adminService.updateMenuItem(editingItem._id, formData);
        showToast('Item atualizado com sucesso!', 'success');
      } else {
        // Create new item using dish
        const dish = dishOptions.find(d => d._id === selectedDishId);
        if (!dish) throw new Error('Prato não selecionado');
        
        const basePayload = {
          nome: dish.nome,
          descricao: dish.descricao,
          categoria: dish.categoria,
          imagem: dish.imagem,
          unidade: formData.unidade,
          ativo: formData.ativo,
          ordem: formData.ordem
        };

        if (multiDateMode && multiDates.length > 0) {
          for (const date of multiDates) {
            await adminService.createMenuItem({ ...basePayload, dataPreparacao: date });
          }
          showToast(`Item criado para ${multiDates.length} datas!`, 'success');
        } else {
          await adminService.createMenuItem({ ...basePayload, dataPreparacao: formData.dataPreparacao });
          showToast('Item criado com sucesso!', 'success');
        }
      }
      
      resetForm();
      loadMenuItems();
    } catch (error) {
      console.error('Error saving menu item:', error);
      if (error.message && error.message.includes('403')) {
        showToast('Acesso negado. Verifique suas permissões.', 'error');
      } else if (error.message && error.message.includes('401')) {
        showToast('Sessão expirada. Faça login novamente.', 'error');
      } else {
        showToast(error.message || 'Erro ao salvar item', 'error');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setMultiDateMode(false);
    setMultiDates([]);
    // Try to find matching dish if possible, but keep original values
    setSelectedDishId(''); // Reset selection as we don't know the dish ID
    setFormData({
      nome: item.nome || '',
      descricao: item.descricao || '',
      categoria: item.categoria || '',
      unidade: item.unidade?._id || '',
      dataPreparacao: item.dataPreparacao ? new Date(item.dataPreparacao).toISOString().split('T')[0] : '',
      imagem: item.imagem || '',
      ativo: item.disponivel !== undefined ? item.disponivel : true, // Map disponivel to ativo
      ordem: item.ordem || 0
    });
    setFormErrors({});
    setHasUnsavedChanges(false);
    setFormLoading(false);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este item?')) {
      return;
    }
    
    try {
      await adminService.deleteMenuItem(id);
      showToast('Item excluído com sucesso!', 'success');
      loadMenuItems();
      setSelectedIds(prev => prev.filter(x => x !== id));
    } catch (error) {
      console.error('Error deleting menu item:', error);
      showToast(error.message || 'Erro ao excluir item', 'error');
    }
  };

  const toggleSelectItem = (id, checked) => {
    setSelectedIds(prev => {
      const set = new Set(prev);
      if (checked) set.add(id); else set.delete(id);
      return Array.from(set);
    });
  };

  const isSelected = (id) => selectedIds.includes(id);

  const selectAllVisible = () => {
    const allIds = menuItems.map(i => i._id);
    setSelectedIds(allIds);
  };

  const clearSelection = () => setSelectedIds([]);

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(`Excluir ${selectedIds.length} item(ns) selecionado(s)? Esta ação não pode ser desfeita.`);
    if (!confirmed) return;

    try {
      const result = await adminService.bulkDeleteMenuItems(selectedIds);
      const { successCount, failureCount } = result;
      if (successCount > 0) {
        showToast(`Excluídos ${successCount} item(ns) com sucesso!`, 'success');
      }
      if (failureCount > 0) {
        showToast(`Falha ao excluir ${failureCount} item(ns).`, 'error');
        console.warn('Bulk delete errors:', result.errors);
      }
      clearSelection();
      loadMenuItems();
    } catch (error) {
      console.error('Bulk delete error:', error);
      showToast(error.message || 'Erro ao excluir itens selecionados', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      categoria: 'prato principal',
      unidade: '',
      dataPreparacao: '',
      imagem: '',
      ativo: true,
      ordem: 0
    });
    setEditingItem(null);
    setShowForm(false);
    setFormErrors({});
    setHasUnsavedChanges(false);
    setFormLoading(false);
    setMultiDateMode(false);
    setMultiDates([]);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Você tem alterações não salvas. Deseja realmente cancelar?')) {
        resetForm();
      }
    } else {
      resetForm();
    }
  };

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
    const icon = type === 'success' ? 'ph-check-circle' : 'ph-x-circle';
    
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform`;
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="ph ${icon} text-lg mr-2"></i>
        <span>${message}</span>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  // Multi-date helpers
  const toLocalDate = (dateStr) => new Date(`${dateStr}T00:00:00`);
  const todayLocalStr = () => {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    const local = new Date(now.getTime() - offsetMs);
    return local.toISOString().split('T')[0];
  };
  const formatDateBR = (dateStr) => {
    try {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Helper: obter label da data de preparação do item
  const getPreparationDateLabel = (item) => {
    const val = item?.dataPreparacao;
    if (!val) return '—';
    if (typeof val === 'string') {
      try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          return formatDateBR(val);
        }
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          // Use UTC timezone to avoid shifting date due to local timezone offset
          return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
        }
        return val;
      } catch (_) {
        return val;
      }
    }
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        // Use UTC timezone to avoid shifting date due to local timezone offset
        return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
      }
    } catch (_) {}
    return '—';
  };
  const addSelectedDate = () => {
    const value = formData.dataPreparacao;
    if (!value) {
      setFormErrors(prev => ({ ...prev, dataPreparacao: 'Selecione uma data para adicionar' }));
      return;
    }
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0,0,0,0);
    if (selectedDate < today) {
      setFormErrors(prev => ({ ...prev, dataPreparacao: 'Data não pode ser anterior a hoje' }));
      return;
    }
    if (multiDates.includes(value)) {
      showToast('Esta data já está na lista', 'error');
      return;
    }
    setMultiDates(prev => [...prev, value]);
    setHasUnsavedChanges(true);
    setFormErrors(prev => { const next = { ...prev }; delete next.dataPreparacao; return next; });
  };

  const removeSelectedDate = (date) => {
    setMultiDates(prev => prev.filter(d => d !== date));
  };

  const groupedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.categoria]) {
      acc[item.categoria] = [];
    }
    acc[item.categoria].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gerenciamento do Cardápio</h2>
            <p className="text-gray-600 mt-1">Gerencie os itens do seu cardápio</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg flex items-center transition-colors w-full sm:w-auto justify-center"
          >
            <i className="ph ph-plus mr-2"></i>
            Adicionar Item
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingItem ? 'Editar Item' : 'Adicionar Item'}
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={formLoading}
                >
                  <i className="ph ph-x text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria do prato</label>
                      <select
                        value={dishCategory}
                        onChange={(e) => setDishCategory(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        disabled={formLoading}
                      >
                        <option value="">Todas</option>
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pesquisar prato</label>
                      <input
                        type="text"
                        value={dishSearch}
                        onChange={(e) => setDishSearch(e.target.value)}
                        placeholder="Nome ou descrição..."
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                        disabled={formLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {editingItem ? 'Alterar Prato (Opcional)' : 'Selecionar Prato'} 
                      {!editingItem && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      value={selectedDishId || ''}
                      onChange={handleDishSelect}
                      className={`w-full px-4 py-3 border rounded-lg transition-colors focus:ring-2 focus:ring-amber-500 focus:border-transparent ${formErrors.dish ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                      disabled={formLoading || dishLoading}
                      required={!editingItem}
                    >
                      <option value="">{editingItem ? 'Manter prato atual (ou selecione para alterar)' : 'Selecione um prato'}</option>
                      {dishLoading && <option value="" disabled>Carregando...</option>}
                      {!dishLoading && dishOptions.map(d => (
                        <option key={d._id} value={d._id}>{d.nome}</option>
                      ))}
                    </select>
                    {formErrors.dish && (
                      <span className="text-sm text-red-600 mt-1 block">{formErrors.dish}</span>
                    )}
                  </div>

                  {/* Preview do item (atual ou selecionado) */}
                  {(formData.nome || selectedDishId) && (
                    <div className="mt-2 p-4 bg-gray-50 border rounded-lg flex items-start gap-4">
                      {formData.imagem && (
                        <img
                          src={formData.imagem}
                          alt={formData.nome}
                          className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )}
                      <div>
                        <div className="font-bold text-gray-900">{formData.nome}</div>
                        <div className="text-sm text-gray-600 mt-1">{formData.descricao}</div>
                        <div className="text-xs text-gray-500 mt-2 px-2 py-1 bg-gray-200 rounded inline-block">
                          {formData.categoria}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unidade <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="unidade"
                      value={formData.unidade}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg transition-colors focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                        formErrors.unidade ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      disabled={formLoading || unitsLoading}
                      required
                    >
                      <option value="">Selecione uma unidade</option>
                      {units.map(unit => (
                        <option key={unit._id} value={unit._id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.unidade && (
                      <span className="text-sm text-red-600 mt-1 block">{formErrors.unidade}</span>
                    )}
                    {unitsLoading && (
                      <span className="text-sm text-gray-500 mt-1 block">Carregando unidades...</span>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Data de Preparação <span className="text-red-500">*</span>
                      </label>
                      {!editingItem && (
                        <label className="flex items-center text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={multiDateMode}
                            onChange={(e) => setMultiDateMode(e.target.checked)}
                            disabled={formLoading}
                          />
                          Múltiplas datas
                        </label>
                      )}
                    </div>
                    {!multiDateMode ? (
                      <>
                        <input
                          type="date"
                          name="dataPreparacao"
                          value={formData.dataPreparacao}
                          onChange={handleInputChange}
                          min={todayLocalStr()}
                          className={`w-full px-4 py-3 border rounded-lg transition-colors focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                            formErrors.dataPreparacao ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                          disabled={formLoading}
                          required
                        />
                        {formErrors.dataPreparacao && (
                          <span className="text-sm text-red-600 mt-1 block">{formErrors.dataPreparacao}</span>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex gap-3">
                          <input
                            type="date"
                            name="dataPreparacao"
                            value={formData.dataPreparacao}
                            onChange={handleInputChange}
                            min={todayLocalStr()}
                            className={`flex-1 px-4 py-3 border rounded-lg transition-colors focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                              formErrors.dataPreparacao ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            disabled={formLoading}
                          />
                          <button
                            type="button"
                            onClick={addSelectedDate}
                            className="px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={formLoading}
                          >
                            Adicionar
                          </button>
                        </div>
                        {formErrors.dataPreparacao && (
                          <span className="text-sm text-red-600 mt-1 block">{formErrors.dataPreparacao}</span>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {multiDates.map((date) => (
                            <span key={date} className="px-2 py-1 bg-gray-100 rounded-full text-sm flex items-center">
                              {formatDateBR(date)}
                              <button
                                type="button"
                                className="ml-2 text-red-600 hover:text-red-800"
                                onClick={() => removeSelectedDate(date)}
                                title="Remover"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {multiDates.length === 0 && (
                            <span className="text-xs text-gray-500">Nenhuma data adicionada</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                    className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded transition-colors"
                    disabled={formLoading}
                  />
                  <label className="ml-3 block text-sm font-medium text-gray-700">
                    Item ativo (visível no cardápio)
                  </label>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={formLoading}
                  >
                    <i className="ph ph-x mr-2"></i>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={formLoading || (hasUnsavedChanges && Object.keys(formErrors).length > 0)}
                  >
                    {formLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className={`ph ${editingItem ? 'ph-check' : 'ph-plus'} mr-2`}></i>
                        {editingItem ? 'Atualizar Item' : 'Criar Item'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Menu Items List */}
      {/* Barra de busca e filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start md:items-end">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-xs leading-tight bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todas</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Unidade</label>
            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-xs leading-tight bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todas</option>
              {unitsLoading && <option value="" disabled>Carregando...</option>}
              {!unitsLoading && units?.map(u => (
                <option key={u._id} value={u._id}>{u.displayName || u.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Ordenar por</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-xs leading-tight bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="createdAt">Data de criação</option>
              <option value="nome">Nome</option>
              <option value="categoria">Categoria</option>
              <option value="dataPreparacao">Data de preparo</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Ordem</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-xs leading-tight bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="asc">Ascendente</option>
              <option value="desc">Descendente</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Pesquisar</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setAppliedSearch(searchQuery); }}
            placeholder="Pesquisar por nome ou descrição..."
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 h-8 text-sm leading-tight focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
      <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <button
          onClick={() => { setSearchQuery(''); setAppliedSearch(''); setSelectedCategory(''); setSortBy('createdAt'); setSortOrder('desc'); setSelectedUnit(''); }}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Limpar filtros
        </button>
          <div className="w-full sm:w-auto flex items-center gap-3 sm:justify-end">
            {filterLoading && (
              <span className="text-sm text-gray-500">Atualizando...</span>
            )}
            <button
              onClick={() => setAppliedSearch(searchQuery)}
              className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-lg transition-colors"
            >
              Pesquisar
            </button>
          <div className="flex items-center gap-2">
            <button
              onClick={selectAllVisible}
              className="text-sm text-gray-600 hover:text-gray-800"
              title="Selecionar todos os itens visíveis"
            >
              Selecionar todos
            </button>
            {selectedIds.length > 0 && (
              <>
                <span className="text-sm text-gray-500">Selecionados: {selectedIds.length}</span>
                <button
                  onClick={handleBulkDelete}
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors"
                >
                  Excluir selecionados
                </button>
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Limpar seleção
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      </div>

      <div className="space-y-6">
        {(selectedCategory ? categories.filter(c => c.value === selectedCategory) : categories).map(category => {
          const items = groupedItems[category.value] || [];
          if (items.length === 0) return null;

          return (
            <div key={category.value} className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {category.label} ({items.length})
              </h3>
              
              <div className="space-y-3 max-h-[60vh] overflow-y-auto overscroll-contain pr-2">
                {items.map(item => (
                  <div key={item._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={isSelected(item._id)}
                        onChange={(e) => toggleSelectItem(item._id, e.target.checked)}
                        className="mr-2 h-4 w-4 text-amber-600 border-gray-300 rounded"
                        aria-label={`Selecionar ${item.nome}`}
                      />
                      {item.imagem && (
                        <img
                          src={item.imagem}
                          alt={item.nome}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h4 className="font-medium text-gray-900">{item.nome}</h4>
                        <p className="text-sm text-gray-600">{item.descricao}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-500">
                            Unidade: {item.unidade?.name || 'Não definida'}
                          </span>
                          {!item.disponivel && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                              Inativo
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Data de preparação: {getPreparationDateLabel(item)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <i className="ph ph-pencil"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <i className="ph ph-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {menuItems.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <i className="ph ph-fork-knife text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum item no cardápio</h3>
          <p className="text-gray-600 mb-4">Comece adicionando o primeiro item ao seu cardápio.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Adicionar Primeiro Item
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;